// System
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { parseISO } from 'date-fns';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { StringTableColumnHeader, TableColumnHeader } from '@Components/shared/tables/columns';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { DateTableColumnHeader } from '@Components/shared/tables/columns/DateTableColumnHeader/DateTableColumnHeader';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { Filterable } from '@contracts/common/traits/Filterable';
import { ProviderIgnoredRequestsItemDto } from '@contracts/api/dto/ProviderIgnoredRequests/ProviderIgnoredRequestsDto';

// Utils
import {
  ProviderIgnoredRequestsListColumns,
  ProviderIgnoredRequestsListFilter,
  providerIgnoredRequestsListColumns,
} from '@utils/tables/providerIgnoredRequests/providerIgnoredRequestsListFilter';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';

// Redux
import { useDispatch } from 'react-redux';
import { useProviderIgnoredRequestsTable } from '@Modal/hooks/providerIgnoredRequests/useProviderIgnoredRequestsTable';

type HeaderOptions = {
  filter: Filterable<ProviderIgnoredRequestsListFilter>;
};

type Columns = TableColumnDescriptionArray<
  ProviderIgnoredRequestsItemDto,
  HeaderOptions,
  typeof providerIgnoredRequestsListColumns
>;

type Column = TableColumnDescription<
  ProviderIgnoredRequestsItemDto,
  HeaderOptions,
  ProviderIgnoredRequestsListColumns
>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useProviderIgnoredRequestsTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const dispatch = useDispatch();
  const { handleFilterSelect, setProviderIgnoredRequestsTableFilters } =
    useProviderIgnoredRequestsTable();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'providerIgnoredRequests.createdAt',
          title: 'Created At',
          sx: { minWidth: '140px', maxWidth: '180px' },
          header: ({ title }) => (
            <DateTableColumnHeader
              title={title}
              onApply={(startDate, endDate) => {
                startDate?.length
                  ? dispatch(
                      setProviderIgnoredRequestsTableFilters({
                        codeName: 'createdAtStart',
                        value: startDate,
                      })
                    )
                  : null;
                endDate?.length
                  ? dispatch(
                      setProviderIgnoredRequestsTableFilters({
                        codeName: 'createdAtEnd',
                        value: endDate,
                      })
                    )
                  : null;
              }}
              onReset={() => {
                dispatch(
                  setProviderIgnoredRequestsTableFilters({
                    codeName: 'createdAtStart',
                    value: undefined,
                  })
                );
                dispatch(
                  setProviderIgnoredRequestsTableFilters({
                    codeName: 'createdAtEnd',
                    value: undefined,
                  })
                );
              }}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.reason',
          title: 'Reason',
          sx: { minWidth: '300px', maxWidth: '300px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'reason'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.reason}</Box>,
        },
        {
          name: 'providerIgnoredRequests.operation',
          title: 'Operation',
          sx: { minWidth: '170px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'operation'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.operation}</Box>,
        },
        {
          name: 'providerIgnoredRequests.playerId',
          title: 'Player ID',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'playerId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.playerId && (
                <TruncateText text={row?.playerId} maxLength={8} textStyles={styles.id} />
              )}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.roundId',
          title: 'Round ID',
          sx: { minWidth: '170px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'roundId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.roundId && (
                <TruncateText text={row?.roundId} maxLength={8} textStyles={styles.id} />
              )}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.betId',
          title: 'Bet ID',
          sx: { minWidth: '180px', maxWidth: '240px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.betId && (
                <TruncateText text={row?.betId} maxLength={8} textStyles={styles.id} />
              )}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.betAmount',
          title: 'Bet Amount',
          sx: { minWidth: '180px', maxWidth: '240px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.betAmount}</Box>,
        },
        {
          name: 'providerIgnoredRequests.providerId',
          title: 'Provider ID',
          sx: { minWidth: '180px', maxWidth: '240px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'providerId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.providerId && (
                <TruncateText text={row?.providerId} maxLength={8} textStyles={styles.id} />
              )}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.startDate',
          title: 'Start Date',
          sx: { minWidth: '180px', maxWidth: '240px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.startDate && (
                <DateLabel date={new Date(parseISO(row?.startDate))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.endDate',
          title: 'End Date',
          sx: { minWidth: '180px', maxWidth: '240px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.endDate && <DateLabel date={new Date(parseISO(row?.endDate))} format="long" />}
            </Box>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
